import React, { useCallback, useMemo, useState } from 'react';
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel } from '@tanstack/react-table';
import { TbTemplate } from 'react-icons/tb';
import { toast } from 'react-toastify';
import useUserTemplateStore from '../../store/user-templates-store';
import useToolStore from '../../store/tools-store';
import useUserStore from '../../store/user-store';
import useOrganisationStore from '../../store/organisations-store';
import usePaginatedListPage from '../../shared/hooks/usePaginatedListPage';
import useSearchableFilter from '../../shared/hooks/useSearchableFilter';
import DataTable from '../../shared/components/table/DataTable';
import PageHeader from '../../shared/components/table/PageHeader';
import SearchInput from '../../shared/components/table/SearchInput';
import SearchableSelect from '../../shared/components/table/SearchableSelect';
import ClearFiltersButton from '../../shared/components/table/ClearFiltersButton';
import ListFooter from '../../shared/components/table/ListFooter';
import RowActions from '../../shared/components/table/RowActions';
import DeleteModal from '../../shared/components/modal/DeleteModal';
import TemplateExample from '../../shared/components/TemplateExample';
import { renderLargeCell } from '../../shared/lib/renderLargeCell';
import UserTemplateFormModal from './UserTemplateFormModal';

const columnHelper = createColumnHelper();

const UserTemplatesPage = () => {
  const {
    userTemplates,
    totalUserTemplates,
    totalPages,
    fetchUserTemplates,
    fetchUserTemplateById,
    createUserTemplate,
    updateUserTemplate,
    deleteUserTemplate,
  } = useUserTemplateStore();
  const { searchUsers } = useUserStore();
  const { tools, fetchTools } = useToolStore();
  const { searchOrganisations } = useOrganisationStore();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [exampleModalOpen, setExampleModalOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedUserTemplate, setSelectedUserTemplate] = useState(null);

  const {
    value: selectedOrg,
    label: selectedOrgLabel,
    onChange: setOrgSelection,
    clear: clearOrgFilter,
    loadOptions: loadOrgOptions,
  } = useSearchableFilter(searchOrganisations);

  const getUserSearchExtras = useCallback(() => ({ orgId: selectedOrg }), [selectedOrg]);
  const {
    value: selectedUser,
    label: selectedUserLabel,
    onChange: setUserSelection,
    clear: clearUserFilter,
    loadOptions: loadUserOptions,
  } = useSearchableFilter(searchUsers, getUserSearchExtras);

  const fetchData = useCallback(
    async (page, search, userId, orgId) => {
      await Promise.all([fetchUserTemplates(page, search, userId, orgId), fetchTools()]);
    },
    [fetchUserTemplates, fetchTools]
  );

  const {
    page: currentPage,
    setPage: setCurrentPage,
    search: globalFilter,
    setSearch: setGlobalFilter,
    isLoading,
  } = usePaginatedListPage({
    fetchItems: fetchData,
    deps: [selectedUser, selectedOrg],
  });

  const openCreate = () => {
    setSelectedUserTemplate(null);
    setFormMode('create');
    setIsFormOpen(true);
  };

  const openEdit = (userTemplate) => {
    setSelectedUserTemplate(userTemplate);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const openDelete = (userTemplate) => {
    setSelectedUserTemplate(userTemplate);
    setIsDeleteModalOpen(true);
  };

  const closeModals = () => {
    setIsFormOpen(false);
    setIsDeleteModalOpen(false);
    setExampleModalOpen(false);
    setSelectedUserTemplate(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (formMode === 'edit' && selectedUserTemplate) {
        await updateUserTemplate(selectedUserTemplate.id, formData);
        toast.success('User template updated successfully');
      } else {
        await createUserTemplate(formData);
        toast.success('User template created successfully');
      }
      closeModals();
    } catch (error) {
      console.error(`Failed to ${formMode === 'edit' ? 'update' : 'create'} userTemplate:`, error);
      toast.error(`Failed to ${formMode === 'edit' ? 'update' : 'create'} user template. Please try again.`);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteUserTemplate(id);
      toast.success('User template deleted successfully');
      closeModals();
    } catch (error) {
      console.error('Failed to delete userTemplate:', error);
      toast.error('Failed to delete user template. Please try again.');
    }
  };

  const clearFilters = () => {
    setGlobalFilter('');
    clearUserFilter();
    clearOrgFilter();
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', { header: 'ID' }),
      columnHelper.accessor('name', { header: 'Name' }),
      columnHelper.accessor('value', {
        header: 'Value',
        cell: (info) => renderLargeCell(expandedRows, setExpandedRows, info, 'value'),
      }),
      columnHelper.accessor('owner_name', {
        header: 'Owner Name',
        cell: ({ row }) => row.original?.owner_name || row.original?.owner_id || '-',
      }),
      columnHelper.accessor('owner_type', {
        header: 'Owner Type',
        cell: ({ row }) => <span className="capitalize">{row.original?.owner_type || '-'}</span>,
      }),
      columnHelper.accessor('tool_name', { header: 'Tool Name' }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        cell: ({ row }) => (
          <RowActions
            className="flex space-x-4 justify-center items-center"
            onEdit={() => openEdit(row.original)}
            onDelete={() => openDelete(row.original)}
            after={
              <TbTemplate
                title="Examples"
                onClick={() => {
                  setSelectedUserTemplate(row.original);
                  setExampleModalOpen(true);
                }}
                size={18}
                className="cursor-pointer text-green-600"
              />
            }
          />
        ),
      }),
    ],
    [expandedRows]
  );

  const table = useReactTable({
    data: userTemplates,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const noUserTemplatesFound = table.getRowModel().rows.length === 0;

  const initialValues = useMemo(() => {
    if (formMode === 'edit' && selectedUserTemplate) {
      return {
        name: selectedUserTemplate.name || '',
        value: selectedUserTemplate.value || '',
        tool_id: selectedUserTemplate.tool_id || '',
        owner_type: selectedUserTemplate.owner_type || 'user',
        owner_id: selectedUserTemplate.owner_id || '',
      };
    }
    return { name: '', value: '', tool_id: '', owner_type: 'user', owner_id: '' };
  }, [formMode, selectedUserTemplate]);

  const selectedOwnerName =
    formMode === 'edit' ? selectedUserTemplate?.owner_name || '' : '';

  return (
    <div className="max-w-screen-3xl mx-auto mt-4">
      <PageHeader title="User Template List" onCreate={openCreate} />
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <SearchInput value={globalFilter} onChange={setGlobalFilter} />
        <SearchableSelect
          name="org_id"
          value={selectedOrg}
          selectedLabel={selectedOrgLabel}
          onChange={(id, option) => {
            setOrgSelection(id, option);
            clearUserFilter();
          }}
          loadOptions={loadOrgOptions}
          placeholder="Select Organisation"
        />
        <SearchableSelect
          name="user_id"
          value={selectedUser}
          selectedLabel={selectedUserLabel}
          onChange={setUserSelection}
          loadOptions={loadUserOptions}
          placeholder="Select User"
        />
        <ClearFiltersButton show={!!(globalFilter || selectedUser || selectedOrg)} onClick={clearFilters} />
      </div>
      <DataTable
        table={table}
        columnsCount={columns.length}
        isLoading={isLoading}
        emptyMessage="No user templates found"
      />
      <ListFooter
        total={totalUserTemplates}
        totalPages={totalPages}
        page={currentPage}
        setPage={setCurrentPage}
        isLoading={isLoading}
        isEmpty={noUserTemplatesFound}
        itemLabel="user templates"
      />

      <UserTemplateFormModal
        isOpen={isFormOpen}
        onClose={closeModals}
        mode={formMode}
        title={formMode === 'edit' ? 'Edit User Template' : 'Create User Template'}
        initialValues={initialValues}
        initialOwnerName={selectedOwnerName}
        onSubmit={handleFormSubmit}
        tools={tools}
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeModals}
        data={{ ...selectedUserTemplate, dataName: 'user template' }}
        onDelete={handleDelete}
      />
      <TemplateExample
        isOpen={exampleModalOpen}
        onClose={closeModals}
        template={selectedUserTemplate}
        setTemplate={setSelectedUserTemplate}
        fetchById={fetchUserTemplateById}
        ownerType="user"
      />
    </div>
  );
};

export default UserTemplatesPage;
